import { apiOrders } from "@/api"
import { cartAtom, profileAtom } from "@/atoms"
import { bottomChromeHeightAtom } from "@/atoms/bottom-chrome"
import cartHydratedAtom from "@/atoms/cart-hydrated"
import { tableAtom } from "@/atoms/table"
import { ActiveOrderBar } from "@/components/orders"
import { SchedulingBanner } from "@/components/scheduling/SchedulingBanner"
import { getUserAddresses, removeProfileToken, verifyUserInCart } from "@/lib"
import { getStoredCompany } from "@/lib/company-storage"
import { getKeptCouponCodes, getPendingCouponCodes } from "@/lib/pending-coupon"
import { fetchLoyaltyRewards } from "@/lib/discount-evaluation/api"
import { trackMetaInitiateCheckout } from "@/lib/meta-pixel"
import { recordStorefrontEvent } from "@/domain/storefront-analytics"
import { fetchLoyalty } from "@/lib/loyalty-service"
import { fetchActiveBenefits, fetchCustomerBenefits, openEntitlements } from "@/lib/loyalty-benefits"
import { Address } from "@/types"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { RESET } from "jotai/utils"
import { parsePhoneNumberWithError } from "libphonenumber-js"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { usePageBackground } from "@/hooks/use-page-background"
import { useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { CheckoutContext, CheckoutContextValue } from "./CheckoutContext"
import { CheckoutSkeleton } from "./components/CheckoutSkeleton"
import { FrozenOutlet } from "./components/FrozenOutlet"
import { Stepper } from "./components/Stepper"
import {
  CheckoutStep,
  applyOrderTiming,
  checkoutAtom,
  getVisibleSteps,
  isScheduleStepVisible,
} from "./state"
import { benefitClaimInstants } from "./lib/checkout-pricing"
import { couponSnapshotAtom, rulePromotionSnapshotAtom } from "./lib/discount-plan"
import { useDiscountEvaluation } from "./hooks/useDiscountEvaluation"
import { lastAppliedCouponsAtom } from "./hooks/useSettleCouponCodes"
import { checkoutStepOf, forgetCheckoutStep, rememberCheckoutStep, resumableCheckoutStep } from "./lib/checkout-resume"
import { useStorefrontScheduling } from "@/hooks/use-storefront-scheduling"
import { Avatar, Button, PageHeader, Text } from "@/components/ui"
import { DURATION, EASE, TRANSITION } from "@/lib/motion"

const STEP_FROM_PATH: Record<string, CheckoutStep> = {
  timing: "timing",
  service: "service",
  schedule: "schedule",
  payment: "payment",
  extras: "extras",
  review: "review",
}

function readStepFromPath(pathname: string): CheckoutStep {
  const segment = pathname.split("/").filter(Boolean).pop() ?? ""
  return STEP_FROM_PATH[segment] ?? "service"
}

/**
 * Room, in px, between the last piece of a step and the fixed footer. Added on
 * top of the footer's measured height: it grows with the hint and its lines.
 */
const STEP_FOOTER_GAP = 32

export function CheckoutLayout() {
  const cart = useAtomValue(cartAtom)
  const cartHydrated = useAtomValue(cartHydratedAtom)
  const [profile, setProfile] = useAtom(profileAtom)
  const [checkoutState, setCheckoutState] = useAtom(checkoutAtom)
  const tableCtx = useAtomValue(tableAtom)
  const bottomChrome = useAtomValue(bottomChromeHeightAtom)
  const isTableOrder = tableCtx !== null
  const navigate = useNavigate()
  const location = useLocation()
  const [addresses, setAddresses] = useState<Address[]>([])
  const [loading, setLoading] = useState(true)
  const [hasActiveCoupons, setHasActiveCoupons] = useState(false)
  const hasInitialized = useRef(false)
  const initiateCheckoutFired = useRef(false)
  const pendingCouponApplied = useRef(false)
  const leftFromStep = useRef<CheckoutStep | null>(null)
  const [resumed] = useState(() => resumableCheckoutStep() !== null)

  const currentStep = readStepFromPath(location.pathname)
  const hasActiveLoyalty = checkoutState.loyalty.program.active === true
  const hasClaimableBenefits =
    openEntitlements(
      checkoutState.benefits.active,
      checkoutState.benefits.available,
      benefitClaimInstants(checkoutState),
    ).length > 0
  const hasRewards = (checkoutState.rewards?.available.length ?? 0) > 0
  const hasExtras = hasActiveCoupons || hasActiveLoyalty || hasClaimableBenefits || hasRewards
  const reduceMotion = useReducedMotion()
  const scheduling = useStorefrontScheduling()
  const setCouponSnapshot = useSetAtom(couponSnapshotAtom)
  const setRulePromotionSnapshot = useSetAtom(rulePromotionSnapshotAtom)
  const setLastAppliedCoupons = useSetAtom(lastAppliedCouponsAtom)

  useDiscountEvaluation(!isTableOrder && !loading)

  const schedulableServiceTypes = useMemo(
    () => scheduling.schedulableChannels.map((channel) => channel.serviceType),
    [scheduling.schedulableChannels],
  )
  const scheduleStepVisible =
    !isTableOrder && isScheduleStepVisible(checkoutState, schedulableServiceTypes)

  const visibleSteps = useMemo(
    () =>
      getVisibleSteps({
        hasExtras,
        isTableOrder,
        showTiming: scheduling.timingStepVisible,
        showSchedule: scheduleStepVisible,
      }),
    [hasExtras, isTableOrder, scheduling.timingStepVisible, scheduleStepVisible],
  )
  const currentIndex = Math.max(0, visibleSteps.indexOf(currentStep))
  const prevIndexRef = useRef(currentIndex)
  const direction = currentIndex >= prevIndexRef.current ? 1 : -1

  useEffect(() => {
    prevIndexRef.current = currentIndex
  }, [currentIndex])

  useEffect(() => {
    if (isTableOrder || !scheduling.known || scheduling.timingStepVisible) return
    setCheckoutState((prev) => applyOrderTiming(prev, scheduling.implicitTiming))
  }, [
    isTableOrder,
    scheduling.known,
    scheduling.timingStepVisible,
    scheduling.implicitTiming,
    setCheckoutState,
  ])

  const stepVariants = {
    enter: (dir: number) =>
      reduceMotion ? { opacity: 0 } : { x: dir * 64, opacity: 0 },
    center: {
      x: 0,
      opacity: 1,
      transition: { duration: DURATION.slow, ease: EASE.enter },
    },
    exit: (dir: number) =>
      reduceMotion
        ? { opacity: 0, transition: { duration: DURATION.fast } }
        : {
            x: dir * -64,
            opacity: 0,
            transition: TRANSITION.exit,
          },
  }

  useEffect(() => {
    if (!cartHydrated) return
    if (cart?.items?.length !== 0) return
    if (!location.pathname.startsWith("/cart/checkout/")) return
    window.location.href = "/"
  }, [cartHydrated, cart?.items?.length, location.pathname])

  useEffect(() => {
    if (initiateCheckoutFired.current) return
    if (!cart?.items?.length) return
    initiateCheckoutFired.current = true
    trackMetaInitiateCheckout(cart)
    recordStorefrontEvent({ type: "checkout_start" })
  }, [cart])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [currentStep])

  usePageBackground()

  useEffect(() => {
    if (hasInitialized.current) return
    hasInitialized.current = true

    let cancelled = false

    async function bootstrap() {
      if (isTableOrder) {
        try {
          await verifyUserInCart()
          if (cancelled) return
          setCheckoutState((prev) => ({ ...prev, serviceType: "INDOOR" }))
          navigate("/cart/checkout/review", { replace: true })
        } catch (err) {
          console.error("[checkout] table bootstrap failed", err)
        } finally {
          if (!cancelled) setLoading(false)
        }
        return
      }

      try {
        await verifyUserInCart()
        const [userAddresses] = await Promise.all([
          getUserAddresses(),
          loadLoyalty(),
          loadCoupons(),
          loadBenefits(),
          loadRewards(),
        ])
        if (cancelled) return
        setAddresses(userAddresses)
      } catch (err) {
        console.error("[checkout] bootstrap failed", err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    async function loadLoyalty() {
      const company = getStoredCompany()
      if (!company) return

      try {
        const { points, program } = await fetchLoyalty(company.id)
        if (cancelled) return
        setCheckoutState((prev) => ({
          ...prev,
          loyalty: {
            ...prev.loyalty,
            pointsBalance: points?.pointsBalance ?? 0,
            program,
          },
        }))
      } catch {
      }
    }

    async function loadCoupons() {
      const company = getStoredCompany()
      if (!company) return

      try {
        const { data } = await apiOrders.get<unknown>(
          `/merchants/${company.id}/coupons/active`,
        )
        if (cancelled) return
        const list = Array.isArray(data) ? data : []
        setHasActiveCoupons(list.length > 0)
      } catch {
        if (!cancelled) setHasActiveCoupons(false)
      }
    }

    async function loadRewards() {
      const company = getStoredCompany()
      if (!company) return

      try {
        const { rewards, balance } = await fetchLoyaltyRewards(company.id)
        if (cancelled) return
        setCheckoutState((prev) => {
          const offered = new Set(rewards.map((reward) => reward.id))
          return {
            ...prev,
            rewards: {
              balance,
              available: rewards,
              redeemed: (prev.rewards?.redeemed ?? []).filter((pick) => offered.has(pick.rewardId)),
            },
          }
        })
      } catch {
      }
    }

    async function loadBenefits() {
      const company = getStoredCompany()
      if (!company) return

      try {
        const [customer, active] = await Promise.all([
          fetchCustomerBenefits(company.id),
          fetchActiveBenefits(company.id).catch(() => null),
        ])
        if (cancelled) return
        setCheckoutState((prev) => ({
          ...prev,
          benefits: {
            active,
            available: (customer.entitlements ?? []).filter(
              (grant) => !grant.expiresAt || new Date(grant.expiresAt).getTime() > Date.now(),
            ),
            selectedIds: prev.benefits.selectedIds,
          },
        }))
      } catch {
      }
    }

    bootstrap()

    return () => {
      cancelled = true
    }
  }, [setCheckoutState, isTableOrder, navigate])

  useEffect(() => {
    if (pendingCouponApplied.current) return
    if (loading || !cartHydrated || isTableOrder) return
    pendingCouponApplied.current = true
    const company = getStoredCompany()
    if (!company) return
    const applied = getPendingCouponCodes(company.id)
    const pending = applied.length > 0 || resumed ? applied : getKeptCouponCodes(company.id)
    if (pending.length === 0) return
    setCheckoutState((prev) => ({
      ...prev,
      couponCodes: [...prev.couponCodes, ...pending.filter((code) => !prev.couponCodes.includes(code))],
    }))
  }, [loading, cartHydrated, isTableOrder, setCheckoutState, resumed])

  useEffect(
    function trackStepForResume() {
      leftFromStep.current = checkoutStepOf(location.pathname)
    },
    [location.pathname],
  )

  useEffect(() => {
    return () => {
      const step = leftFromStep.current
      const orderPlaced = window.location.pathname.startsWith("/cart/order-confirmed")
      if (step && !orderPlaced) {
        rememberCheckoutStep(step)
      } else {
        forgetCheckoutStep()
        setCheckoutState(RESET)
      }
      setCouponSnapshot({ status: "idle" })
      setRulePromotionSnapshot({ status: "idle" })
      setLastAppliedCoupons([])
    }
  }, [setCheckoutState, setCouponSnapshot, setRulePromotionSnapshot, setLastAppliedCoupons])

  useEffect(() => {
    if (profile && addresses.length > 0) {
      setProfile({
        ...profile,
        addresses,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addresses])

  const contextValue = useMemo<CheckoutContextValue>(
    () => ({
      addresses,
      hasExtras,
      isTableOrder,
      tableName: tableCtx?.name ?? "",
      tableId: tableCtx?.id ?? "",
      tableExternalCode: tableCtx?.externalCode ?? "",
    }),
    [addresses, hasExtras, isTableOrder, tableCtx],
  )

  if (loading || !profile) {
    return <CheckoutSkeleton />
  }

  return (
    <CheckoutContext.Provider value={contextValue}>
      <motion.div
        style={{ paddingBottom: bottomChrome + STEP_FOOTER_GAP }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={TRANSITION.size}>
        <PageHeader
          raised
          surface="card"
          gutter="inset"
          offsetActiveOrderBar
          onBack={() => {
            if (currentIndex <= 0) {
              navigate("/cart", { replace: true })
            } else {
              navigate(`/cart/checkout/${visibleSteps[currentIndex - 1]}`, { replace: true })
            }
          }}
          identity={
            <>
              <Avatar size="md" />
              <div className="min-w-0">
                <Text weight="semibold" tone="default" truncate>
                  {profile.name}
                </Text>
                <Text size="sm" tone="muted" truncate>
                  {parsePhoneNumberWithError(profile.phone.number, "BR").formatNational()}
                </Text>
              </div>
            </>
          }
          trailing={
            <Button
              variant="link"
              tone="danger"
              size="auto"
              onClick={() => removeProfileToken()}
              className="underline-offset-auto"
            >
              <Text as="span" size="sm">
                Não é você?
              </Text>
            </Button>
          }
          bottom={
            <Stepper
              currentStep={currentStep}
              hasExtras={hasExtras}
              isTableOrder={isTableOrder}
              showTiming={scheduling.timingStepVisible}
              showSchedule={scheduleStepVisible}
            />
          }
        />
        <ActiveOrderBar inline forceVisible />
        <SchedulingBanner />
        <main className="relative min-h-empty overflow-x-clip">
          <AnimatePresence mode="popLayout" initial={false} custom={direction}>
            <motion.div
              key={currentStep}
              custom={direction}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              <FrozenOutlet step={currentStep} stepFromPath={readStepFromPath} />
            </motion.div>
          </AnimatePresence>
        </main>
      </motion.div>
    </CheckoutContext.Provider>
  )
}
